import { Injectable, NgZone } from '@angular/core';
import { LocalStorageService } from './local-storage.service';

export interface VoiceSettings {
  microphoneId: string; speakerId: string; voices: Record<string, string>;
  /** Device ID each passed test was for; a different saved device needs a new test. */
  testedDevices: Partial<Record<'speaker' | 'microphone', string>>;
}

export interface VoiceEvent {
  type: string;
  text?: string;
  id?: string;
  role?: 'user' | 'assistant';
  callId?: string;
  offsetMs?: number;
  endMs?: number;
  delegationId?: string;
}
/** Owns only the browser media connection. Conversation owns the target and messages. */
@Injectable({ providedIn: 'root' })
export class ConversationVoiceService {
  /** A recoverable provider notice shown while the call continues. It is advice to the live caller,
   *  not a transport failure, so an intentional/confirmed hangup must clear it (a fatal error, which
   *  carries a different message, is left intact). */
  static readonly recoverableNotice = 'GPT-Live could not complete the last request. You can keep talking or end the call.';
  state: 'idle' | 'connecting' | 'live' | 'error' = 'idle';
  error = '';
  private generation = 0;
  private peer?: RTCPeerConnection;
  private microphone?: MediaStream;
  private playback?: HTMLAudioElement;
  private abort?: AbortController;
  callId?: string;
  private channel?: RTCDataChannel;
  private closing?: Promise<void>;
  private finishClose?: () => void;
  private timer?: ReturnType<typeof setTimeout>;
  constructor(private readonly zone: NgZone, private readonly storage: LocalStorageService) {}

  /** Changes on every start or stop, so callers can drop a start request that a stop superseded. */
  get version(): number { return this.generation; }

  /** Per-person browser preferences. `voices` holds the speaking voice chosen for each Live provider. */
  get settings(): VoiceSettings {
    const saved = this.storage.get<{ microphoneId?: unknown; speakerId?: unknown; voices?: unknown; voice?: unknown; testedDevices?: unknown }>('conversation-voice@1');
    const voices: Record<string, string> = {};
    if (saved?.voices && typeof saved.voices === 'object')
      for (const [provider, name] of Object.entries(saved.voices)) if (typeof name === 'string') voices[provider] = name;
    // Earlier single-provider preference: GPT-Live (openai) was the only provider.
    if (!voices['openai'] && typeof saved?.voice === 'string') voices['openai'] = saved.voice;
    const testedDevices: VoiceSettings['testedDevices'] = {};
    const tested = saved?.testedDevices as Record<string, unknown> | undefined;
    for (const kind of ['speaker', 'microphone'] as const) if (typeof tested?.[kind] === 'string') testedDevices[kind] = tested[kind] as string;
    return { microphoneId: typeof saved?.microphoneId === 'string' ? saved.microphoneId : '', speakerId: typeof saved?.speakerId === 'string' ? saved.speakerId : '', voices, testedDevices };
  }
  saveSettings(change: Partial<VoiceSettings>): boolean {
    return this.storage.set('conversation-voice@1', { ...this.settings, ...change });
  }

  get canSelectSpeaker(): boolean { return typeof HTMLMediaElement.prototype.setSinkId === 'function'; }

  /** Shared by test playback and conversation playback; never silently use a different speaker. */
  async setSpeaker(playback: HTMLAudioElement, deviceId: string): Promise<void> {
    if (!deviceId) return;
    if (typeof playback.setSinkId !== 'function')
      throw new Error('This browser cannot select a speaker. Choose System default or use a supported browser.');
    await playback.setSinkId(deviceId);
  }

  /** Test resources belong to the requesting panel, independently of any live conversation. */
  async testSpeaker(deviceId: string, signal: AbortSignal, failed: (message: string) => void): Promise<void> {
    if (signal.aborted) return;
    const playback = new Audio('assets/audio/speaker-test.wav');
    playback.loop = true; playback.volume = 0.4;
    const cleanup = () => {
      playback.onerror = null;
      playback.pause(); playback.removeAttribute('src'); playback.load();
      signal.removeEventListener('abort', cleanup);
    };
    signal.addEventListener('abort', cleanup, { once: true });
    playback.onerror = () => { cleanup(); this.zone.run(() => failed('The test sound could not be played. Try again.')); };
    try {
      await this.setSpeaker(playback, deviceId);
      if (signal.aborted) return;
      await playback.play();
      if (signal.aborted) cleanup();
    } catch (error) { cleanup(); throw error; }
  }

  async testMicrophone(deviceId: string, signal: AbortSignal, level: (value: number) => void,
    failed: (message: string) => void): Promise<void> {
    if (signal.aborted) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof AudioContext === 'undefined')
      throw new Error('Microphone testing requires a supported browser on HTTPS.');
    const context = new AudioContext();
    let stream: MediaStream | undefined;
    let source: MediaStreamAudioSourceNode | undefined;
    let timer: ReturnType<typeof setInterval> | undefined;
    const cleanup = () => {
      if (timer !== undefined) clearInterval(timer);
      stream?.getTracks().forEach(track => { track.onended = null; track.stop(); });
      source?.disconnect();
      if (context.state !== 'closed') void context.close().catch(() => {});
      signal.removeEventListener('abort', cleanup);
    };
    signal.addEventListener('abort', cleanup, { once: true });
    try {
      await context.resume();
      if (signal.aborted) return;
      const granted = await navigator.mediaDevices.getUserMedia({ audio: deviceId ? { deviceId: { exact: deviceId } } : true });
      if (signal.aborted) { granted.getTracks().forEach(track => track.stop()); return; }
      stream = granted;
      stream.getAudioTracks().forEach(track => track.onended = () => {
        cleanup(); this.zone.run(() => failed('Microphone disconnected. Select an available device and retry.'));
      });
      const analyser = context.createAnalyser(); analyser.fftSize = 1024;
      source = context.createMediaStreamSource(stream);
      // Analyse locally without connecting to the speakers (no feedback or upload).
      source.connect(analyser);
      const samples = new Float32Array(analyser.fftSize);
      this.zone.runOutsideAngular(() => {
        timer = setInterval(() => {
          if (signal.aborted) return;
          analyser.getFloatTimeDomainData(samples);
          const rms = Math.sqrt(samples.reduce((sum, sample) => sum + sample * sample, 0) / samples.length);
          this.zone.run(() => level(Math.min(1, rms * 8)));
        }, 100);
      });
    } catch (error) { cleanup(); throw error; }
  }

  async start(url: string, body: Record<string, unknown>, token: string,
    receive: (event: VoiceEvent) => void): Promise<void> {
    const stopping = this.stop();
    const generation = this.generation;
    await stopping;
    if (generation !== this.generation) return;
    const current = () => generation === this.generation;
    const settings = this.settings;
    this.state = 'connecting'; this.error = '';
    const abort = new AbortController(); this.abort = abort;
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof RTCPeerConnection === 'undefined')
        throw new Error('This browser does not support Live voice. Use a secure HTTPS connection.');
      const microphone = await navigator.mediaDevices.getUserMedia({ audio: settings.microphoneId ? { deviceId: { exact: settings.microphoneId } } : true });
      if (!current()) { microphone.getTracks().forEach(track => track.stop()); return; }
      this.microphone = microphone;
      // Keep capture muted until both SDP and the GPT-Live session are ready.
      microphone.getAudioTracks().forEach(track => track.enabled = false);
      const peer = new RTCPeerConnection(); this.peer = peer;
      const playback = new Audio(); playback.autoplay = true; this.playback = playback;
      await this.setSpeaker(playback, settings.speakerId);
      if (!current()) return;
      peer.ontrack = event => {
        if (!current()) return;
        playback.srcObject = event.streams[0] ?? new MediaStream([event.track]);
        void playback.play().catch(() => this.zone.run(() => { if (current()) this.fail('Audio playback was blocked. End the call and retry.'); }));
      };
      let sessionStarted = false;
      const connected = () => {
        if (!current() || !sessionStarted || peer.connectionState !== 'connected') return;
        this.state = 'live';
        // Capture was held muted until both SDP and the GPT-Live session were ready; open it now.
        microphone.getAudioTracks().forEach(track => track.enabled = true);
        if (this.timer) clearTimeout(this.timer);
      };
      peer.onconnectionstatechange = () => this.zone.run(() => {
        if (!current()) return;
        if (peer.connectionState === 'connected') {
          connected();
        } else if (['failed', 'disconnected', 'closed'].includes(peer.connectionState) && this.state !== 'idle') {
          this.fail('Voice disconnected. Your accepted tasks may still be running.');
        }
      });
      microphone.getTracks().forEach(track => {
        peer.addTrack(track, microphone);
        track.onended = () => { if (current()) this.zone.run(() => this.fail('Microphone disconnected.')); };
      });
      const channel = peer.createDataChannel('oai-events'); this.channel = channel;
      const seen = new Set<string>();
      channel.onmessage = message => this.zone.run(() => {
        let event: { type?: string; event_id?: string; delta?: string; start_ms?: number; end_ms?: number; offset_ms?: number; delegation?: { id?: string; target?: string } };
        try { event = JSON.parse(message.data); } catch { return; }
        // After a local hangup, this channel still owes its final transcript to the original
        // callback until closure is confirmed; it can no longer start or delegate work.
        const live = current(), draining = !live && this.channel === channel && !!this.finishClose;
        if (!live && !draining) return;
        if (event.event_id) { if (seen.has(event.event_id)) return; seen.add(event.event_id); }
        if (event.type === 'session.closed') {
          receive({ type: 'closed' });
          if (draining) this.finishClose!(); else void this.stop(true);
        } else if (event.type === 'session.input_transcript.delta' || event.type === 'session.output_transcript.delta') {
          if (typeof event.delta === 'string') receive({ type: 'transcript', text: event.delta,
            role: event.type === 'session.input_transcript.delta' ? 'user' : 'assistant',
            id: event.event_id, offsetMs: event.start_ms, endMs: event.end_ms });
        } else if (!live) return;
        else if (event.type === 'session.started') { sessionStarted = true; connected(); }
        // A provider error event (e.g. a rejected command or moderation hold) need not end the
        // session and may carry no event_id; surface a controlled notice and keep the call alive.
        // Actual teardown stays with session.closed and the transport handlers below.
        else if (event.type === 'error') this.error = ConversationVoiceService.recoverableNotice;
        else if (event.type === 'session.delegation.created' && event.delegation?.target === 'client' && event.delegation.id) {
          receive({ type: 'delegation', delegationId: event.delegation.id, offsetMs: event.offset_ms });
        }
      });
      channel.onclose = () => { if (current()) this.zone.run(() => this.fail('Voice disconnected before closure was confirmed. Accepted tasks may still be running.')); };
      channel.onerror = () => { if (current()) this.zone.run(() => this.fail('Voice event connection failed.')); };
      await peer.setLocalDescription(await peer.createOffer());
      if (!current()) return;
      // Gather candidates before forwarding the SDP; this endpoint does not trickle ICE.
      if (peer.iceGatheringState !== 'complete') await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => { cleanup(); reject(new Error('Microphone connection timed out.')); }, 10000);
        const changed = () => { if (peer.iceGatheringState === 'complete') { cleanup(); resolve(); } };
        const cancelled = () => { cleanup(); reject(new DOMException('Cancelled', 'AbortError')); };
        const cleanup = () => { clearTimeout(timeout); peer.removeEventListener('icegatheringstatechange', changed); abort.signal.removeEventListener('abort', cancelled); };
        peer.addEventListener('icegatheringstatechange', changed); abort.signal.addEventListener('abort', cancelled, { once: true });
        changed();
      });
      if (!current()) return;
      this.timer = setTimeout(() => this.zone.run(() => { if (current() && this.state === 'connecting') this.fail('Voice connection timed out.'); }), 45000);
      const response = await fetch(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ ...body, voices: settings.voices, sdp: peer.localDescription?.sdp }), signal: abort.signal,
      });
      const result = await response.json().catch(() => null);
      if (!current()) return;
      if (!response.ok || typeof result?.sdp !== 'string' || typeof result?.id !== 'string')
        throw new Error(result?.message || 'Could not connect GPT-Live. Check Live voice API settings.');
      this.callId = result.id;
      await peer.setRemoteDescription({ type: 'answer', sdp: result.sdp });
    } catch (error) {
      if (current()) this.zone.run(() => this.fail(error instanceof Error ? error.message : 'Could not start voice.'));
    }
  }

  /** Return a result only to the call that requested it; stale completions never reach another agent. */
  append(callId: string | undefined, content: string, delegationId: string | null = null, spoken = true): boolean {
    if (!callId || callId !== this.callId || this.state !== 'live' || this.channel?.readyState !== 'open') return false;
    // Bound each append below 500 tokens even for multibyte text (at most 400 UTF-8 bytes).
    const encoder = new TextEncoder(); let chunk = '', bytes = 0;
    const send = () => this.channel!.send(JSON.stringify({ type: spoken ? 'session.commentary.append' : 'session.thinking.append',
      event_id: crypto.randomUUID(), delegation_id: delegationId, content: chunk }));
    try {
      for (const char of content) {
        const length = encoder.encode(char).length;
        if (bytes + length > 400) { send(); chunk = ''; bytes = 0; }
        chunk += char; bytes += length;
      }
      if (chunk) send();
      return true;
    } catch { this.fail('Could not send the voice update. The task result remains in the conversation.'); return false; }
  }
  async stop(confirmed = false): Promise<void> {
    ++this.generation;
    // Ending on purpose clears a lingering recoverable "you can keep talking" notice; a fatal
    // transport/closure error (a different message, or one set below) is not touched.
    if (this.error === ConversationVoiceService.recoverableNotice) this.error = '';
    if (this.closing) return this.closing;
    const peer = this.peer, channel = this.channel, abort = this.abort;
    this.abort = undefined; abort?.abort();
    if (this.timer) clearTimeout(this.timer); this.timer = undefined;
    this.microphone?.getTracks().forEach(track => { track.onended = null; track.stop(); }); this.microphone = undefined;
    if (this.playback) { this.playback.pause(); this.playback.srcObject = null; this.playback = undefined; }
    this.state = 'idle'; this.callId = undefined;
    if (peer) peer.onconnectionstatechange = null;
    if (channel) { channel.onclose = null; channel.onerror = null; }
    const cleanup = () => { channel?.close(); peer?.close(); this.channel = undefined; this.peer = undefined; this.finishClose = undefined; this.closing = undefined; };
    if (!confirmed && channel?.readyState === 'open') {
      let complete!: () => void;
      const closing = new Promise<void>(resolve => { complete = resolve; });
      this.closing = closing;
      const timeout = setTimeout(() => { this.error = 'Voice ended locally; final session closure was not confirmed.'; cleanup(); complete(); }, 5000);
      this.finishClose = () => { clearTimeout(timeout); cleanup(); complete(); };
      try { channel.send(JSON.stringify({ type: 'session.close' })); }
      catch { this.error = 'Voice ended locally; final session closure was not confirmed.'; this.finishClose(); }
      return closing;
    }
    cleanup();
  }
  private fail(message: string): void {
    void this.stop();
    this.error = message; this.state = 'error';
  }
}
